import {
  Component,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  signal,
  effect,
  SimpleChanges,
  OnChanges,
  ViewChild,
} from '@angular/core';
import {
  PatientPlaylistModel,
  PatientProgramModel,
  PatientProgramStatus,
  PatientTargetModel,
  PatientTargetStatus,
  SkillAreaModel,
} from '../../../core/api/models';
import { SupervisorApiService } from '../../../core/api/services';
import { Router } from '@angular/router';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { SupervisorBaseComponent } from '../../supervisor-base.component';
import { AuthService } from '../../../auth/auth.service';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';
import { AcademyModeService } from '../../../core/services/academy-mode.service';
import {
  getPatientProgramStatusAtAGlanceColor,
  getPatientProgramStatusAtAGlanceText,
  getPatientProgramStatuses,
} from '../../../shared/utils/utils';
import {
  ChangeLogModalComponent,
} from '../../../shared/components/change-log-modal/change-log-modal.component';
import { SparkLogEntityType } from '../../../core/api/models';
@Component({
  selector: 'app-program',
  templateUrl: './program.component.html',
  styleUrl: '../../supervisor-main.component.css',
  standalone: false,
})
export class ProgramComponent
  extends SupervisorBaseComponent
  implements OnInit, OnChanges
{
  patientID = input.required<string>();
  _program: PatientProgramModel | undefined;
  @Input()
  get program(): PatientProgramModel | undefined {
    return this._program;
  }
  set program(value: PatientProgramModel | undefined) {
    this._program = value;
    this.filterTargetsToShow();
  }

  programAlerts = signal<any[]>([]);
  colorPatternStyle = Utils.colorPatternStyle;
  _includeDeleted = false;
  _includeArchived = false;

  isEditingProgramNotes = false;

  TargetsToShow: PatientTargetModel[] = [];

  @Output() isExpandedChange = new EventEmitter<boolean>();

  // Input for external target expansion states
  @Input() targetExpandedStatus: { [key: number]: boolean } = {};
  @Input() statusesToFilter: PatientTargetStatus[] = [];

  // Selection functionality inputs from parent
  @Input() isSelecting: boolean = false;
  @Input() selectAll: boolean = false;

  // Output for target expansion changes to parent
  @Output() targetExpandedChange = new EventEmitter<{
    targetId: number;
    isExpanded: boolean;
  }>();

  // Selection functionality
  programSelected: boolean = false;
  @Output() selectProgram = new EventEmitter<number>();

  programTargetsExpandedStatus: { [key: number]: boolean } = {};

  // Status circle quick edit
  programStatuses: any[] = getPatientProgramStatuses();
  newProgramStatus: PatientProgramStatus | null = null;
  showStatusCircleEdit = false;
  updatingStatus = false;
  showStatusAnnotationDialog = signal<boolean>(false);
  statusAnnotationText = signal('');
  readonly statusAnnotationMaxLength = 30;
  private pendingPropagateStatus = false;
  private pendingProgramStatus: PatientProgramStatus | null = null;
  private pendingProgramID: number | null = null;

  @ViewChild('changeLogModal') changeLogModal!: ChangeLogModalComponent;
  entityTypeProgram = SparkLogEntityType.Program;

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private router: Router,
    patientDataFacadeService: PatientDataFacadeService,
    public academyModeService: AcademyModeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
  }

  ngOnInit(): void {
    console.log('ProgramComponent ngOnInit');

    // if program notes is new, show it as an alert
    if (this.program?.IsProgramNoteNew) {
      this.programAlerts.set([
        {
          id: 1,
          message: this.program?.ProgramNotes ?? '',
          sender: this.program?.NotesLastModifiedByUserFullName ?? '',
          type: 'warning',
        },
      ]);
    }

    this.filterTargetsToShow();
    this.syncTargetExpansionStates();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['targetExpandedStatus']) {
      this.syncTargetExpansionStates();
    }
    if (changes['isSelecting']) {
      this.showStatusCircleEdit = false;
      if (this.isSelecting == false) {
        this.programSelected = false;
      }
    }
    if (changes['selectAll']) {
      if (this.selectAll == true) {
        this.programSelected = true;
        this.selectProgram.emit(this.program?.ID ?? 0);
      } else this.programSelected = false;
    }
  }

  // Sync external target expansion states with internal ones
  private syncTargetExpansionStates() {
    if (this.targetExpandedStatus) {
      // Merge external states with internal ones, external states take precedence
      this.programTargetsExpandedStatus = {
        ...this.programTargetsExpandedStatus,
        ...this.targetExpandedStatus,
      };
    }
  }

  _isExpanded = false;

  @Input()
  get isExpanded(): boolean {
    return this._isExpanded;
  }

  set isExpanded(val: boolean) {
    this._isExpanded = val;
    this.isExpandedChange.emit(this._isExpanded);
  }

  @Input()
  get includeDeleted(): boolean {
    return this._includeDeleted;
  }

  set includeDeleted(val: boolean) {
    this._includeDeleted = val;
    this.filterTargetsToShow();
  }

  @Input()
  get includeArchived(): boolean {
    return this._includeArchived;
  }

  set includeArchived(val: boolean) {
    this._includeArchived = val;
    this.filterTargetsToShow();
  }

  filterTargetsToShow() {
    // if includeDeleted is false, then filter the targets to show
    let programTargetsWithUndefined: (PatientTargetModel | undefined)[] =
      this.program?.Targets?.filter((t) => !!t.ID).map((t) =>
        this.targetForID(t.ID!)
      ) ?? [];
    let programTargets: PatientTargetModel[] =
      programTargetsWithUndefined.filter(
        (t) => t != undefined
      ) as PatientTargetModel[];
    if (!this._includeDeleted) {
      this.TargetsToShow = programTargets.filter((t) => !t?.IsDeleted) ?? [];
    } else {
      this.TargetsToShow = programTargets ?? [];
    }

    // if includeArchived is false, then filter the targets to show
    if (!this._includeArchived) {
      this.TargetsToShow = this.TargetsToShow.filter((t) => !t.IsArchived);
    }

    // if statusesToFilter is not empty, then filter the targets to show
    if (this.statusesToFilter != null && this.statusesToFilter.length > 0) {
      this.TargetsToShow = this.TargetsToShow.filter((t) =>
        this.statusesToFilter.includes(
          t.TargetStatus ?? PatientTargetStatus.Ready
        )
      );
    }

    // Status order first (same priority as server), then saved program order (OrderNr) within same status
    const statusSortKey = (t: PatientTargetModel) => {
      let r = 0;
      if (t.TargetStatus === PatientTargetStatus.Aquisition) r = 0;
      else if (t.TargetStatus === PatientTargetStatus.Maintenance) r = 1;
      else if (t.TargetStatus === PatientTargetStatus.Ready) r = 2;
      else if (t.TargetStatus === PatientTargetStatus.Baseline) r = 3;
      else if (t.TargetStatus === PatientTargetStatus.Mastered) r = 4;
      else if (t.TargetStatus === PatientTargetStatus.On_Hold) r = 5;
      else if (t.TargetStatus === PatientTargetStatus.Closed) r = 6;
      else if (t.TargetStatus === PatientTargetStatus.Fluent) r = 6;
      else r = 7;
      if (t.IsArchived) r += 100;
      if (t.IsDeleted) r += 200;
      return r;
    };
    const programTargetOrder = (id: number) =>
      this.program?.Targets?.find((t) => t.ID === id)?.OrderNr ?? Number.MAX_SAFE_INTEGER;
    this.TargetsToShow.sort((a, b) => {
      const statusA = statusSortKey(a);
      const statusB = statusSortKey(b);
      if (statusA !== statusB) return statusA - statusB;
      return (programTargetOrder(a.ID ?? 0) - programTargetOrder(b.ID ?? 0));
    });
  }

  onTargetExpandedChange(targetId: number, isExpanded: boolean) {
    const key = targetId;
    this.programTargetsExpandedStatus[key] = isExpanded;
    // Emit to parent component
    this.targetExpandedChange.emit({ targetId, isExpanded });
  }

  get skillAreasToDisplay() {
    let programSkillArea = this.supervisorHelperService
      .skillAreas()
      .find((skillArea) => skillArea.ID === this.program?.SkillAreaID);
    if (programSkillArea) {
      return programSkillArea.SkillAreaName;
    } else {
      return 'none';
    }
  }

  toggleCollapsed() {
    if (!this.isSelecting) {
      this.isExpanded = !this.isExpanded;
      this.showStatusCircleEdit = false;
      const tourEvent = new Event('program-clicked');
      document.getElementById('programs')?.dispatchEvent(tourEvent);
      console.log(this.program);
    } else {
      this.programSelected = !this.programSelected;
      this.selectProgram.emit(this.program?.ID ?? 0);
    }
  }

  editProgram() {
    console.log('editProgram clicked!');
    // Store the current expansion state in the parent component before editing
    this.isExpandedChange.emit(this.isExpanded);
    this.supervisorHelperService.additProgramButtonClicked(
      this.program ?? null
    );
  }
  restoreProgram() {
    if (this.program?.ID != null) {
      this.restoreProgramServiceCall();
    }
  }

  goToStaffManagementReport() {
    const patientIdNumber = Number(this.patientID());
    const queryParams: any = {};

    if (this.program?.ID) {
      queryParams.programID = this.program.ID;
    }

    this.router.navigate(
      ['/supervisor', patientIdNumber, 'management-report'],
      { queryParams }
    );
  }

  targetForID(targetID: number) {
    return this.supervisorHelperService
      .targets()
      .find((t) => t.ID === targetID);
  }

  deleteProgram() {
    console.log('deleteProgram clicked!');

    if (this.program?.ID != null) {
      this.alertService
        .confirm(
          'Delete Program',
          'Are you sure you want to delete this program? It will be removed from the playlists it was part of, if any.'
        )
        .subscribe((result) => {
          if (result) {
            if ((this.program?.Targets?.length ?? 0) > 0) {
              setTimeout(() => {
                this.alertService
                  .confirm(
                    'Delete Targets',
                    'You´re deleting a program that has targets. Do you want to delete the targets as well?'
                  )
                  .subscribe((result2) => {
                    if (result2) {
                      this.executeDeleteProgram(true);
                    } else {
                      this.executeDeleteProgram(false);
                    }
                  });
              }, 100);
            } else {
              this.executeDeleteProgram(false);
            }
          }
        });
    }
  }

  executeDeleteProgram(propagate: boolean = false) {
    if (this.supervisorHelperService.playlists.length === 0) {
      this.deleteProgramServiceCall(propagate);
      return;
    } else {
      // remove program from all its playlists
      const removeProgramPromises = this.supervisorHelperService
        .playlists()
        .map((playlist) => this.removeProgramFromPlaylist(playlist));

      Promise.all(removeProgramPromises)
        .then(() => {
          this.deleteProgramServiceCall(propagate);
        })
        .catch((error) => {
          console.error(error);
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
        });
    }
  }

  removeProgramFromPlaylist(playlist: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (playlist.Items == null || playlist.Items.length === 0) {
        resolve();
      } else {
        let programItem = playlist.Items.find(
          (item: { PatientProgramID: number | undefined }) =>
            item.PatientProgramID === this.program?.ID
        );
        if (programItem != null) {
          // create new playlist without program
          let newPlaylist = {
            ID: playlist.ID,
            Items: playlist.Items.filter(
              (item: { PatientProgramID: number | undefined }) =>
                item.PatientProgramID !== this.program?.ID
            ),
            PatientID: playlist.PatientID,
            PlaylistName: playlist.PlaylistName,
          };
          // update playlist
          this.supervisorApiService
            .SavePatientPlaylist({
              body: newPlaylist,
            })
            .subscribe(
              () => {
                resolve();
              },
              (error) => {
                console.error(error);
                this.alertService.error(
                  Utils.getErrorMessageFromErrorResponse(error)
                );
                reject(error);
              }
            );
        } else {
          resolve();
        }
      }
    });
  }

  deleteProgramServiceCall(propagate: boolean = false) {
    this.supervisorApiService
      .MarkPatientProgramAsDeleted({
        patientProgramID: this.program?.ID,
        propagate: propagate,
      })
      .subscribe(
        () => {
          this.alertService.success('Program marked as deleted');
          this.supervisorHelperService.triggerUpdateSignal();
        },
        (error) => {
          console.error(error);
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
        }
      );
  }

  restoreProgramServiceCall() {
    this.supervisorApiService
      .RestorePatientProgram({ patientProgramID: this.program?.ID })
      .subscribe(
        () => {
          this.alertService.success('Program has been restored');
          this.supervisorHelperService.triggerUpdateSignal();
        },
        (error) => {
          console.error(error);
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
        }
      );
  }

  dismissAlertButtonClicked(alert: any) {
    console.log('dismissAlertButtonClicked clicked!');
    this.supervisorApiService
      .MarkProgramNoteAsRead({
        programId: this.program?.ID,
      })
      .subscribe(() => {
        this.programAlerts.set([]);
      });
  }

  startEditingProgramNotes() {
    this.isEditingProgramNotes = true;
  }

  cancelEditingProgramNotes() {
    this.isEditingProgramNotes = false;
  }

  saveProgramNotes() {
    this.staffApiService
      .ChangeProgramNotes({
        programId: this.program?.ID,
        newNotes: this.program?.ProgramNotes ?? '',
        shouldMarkAsNew: false,
      })
      .subscribe(() => {
        this.isEditingProgramNotes = false;
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  lastModifiedByText(): string {
    if (
      !this.program?.NotesLastModifiedByUserFullName ||
      !this.program?.NotesLastModifiedDate
    ) {
      return '';
    }
    return (
      'Last modified by: ' +
      this.program.NotesLastModifiedByUserFullName +
      ' at '
    );
  }

  getCircleColor() {
    return getPatientProgramStatusAtAGlanceColor(this.program);
  }

  getCircleTooltip() {
    return getPatientProgramStatusAtAGlanceText(this.program);
  }

  toggleStatusCircleEdit(e: any) {
    e.stopPropagation();
    if (this.isSelecting == true) {
      return;
    }
    this.newProgramStatus = this.program?.ProgramStatus ?? null;
    this.showStatusCircleEdit = !this.showStatusCircleEdit;
  }

  updateProgramStatus() {
    if (this.updatingStatus || !this.program?.ID) {
      return;
    }
    if (!this.newProgramStatus) {
      this.alertService.error('Select a program status before updating');
      return;
    }

    const programID = this.program.ID;
    const newStatus = this.newProgramStatus;
    this.showStatusCircleEdit = false;
    if (newStatus === this.program.ProgramStatus) {
      return;
    }

    this.alertService
      .confirm(
        'Changing program status',
        "You're changing the program status. Do you want to also change its targets' statuses?"
      )
      .subscribe((propagateStatus) => {
        if (
          newStatus !== PatientProgramStatus.Closed &&
          newStatus !== PatientProgramStatus.On_Hold
        ) {
          this.saveProgramStatus(programID, newStatus, !!propagateStatus, null);
          return;
        }

        this.pendingPropagateStatus = !!propagateStatus;
        this.pendingProgramID = programID;
        this.pendingProgramStatus = newStatus;
        const programName = (this.program?.ProgramName || 'Program').trim();
        const statusSuffix =
          newStatus === PatientProgramStatus.On_Hold ? 'on hold' : 'closed';
        this.statusAnnotationText.set(
          `${programName} ${statusSuffix}`.substring(
            0,
            this.statusAnnotationMaxLength
          )
        );
        this.showStatusAnnotationDialog.set(true);
      });
  }

  statusAnnotationDialogTitle(): string {
    return this.pendingProgramStatus === PatientProgramStatus.On_Hold
      ? 'Moving Program to On Hold'
      : 'Moving Program to Closed';
  }

  confirmStatusAnnotationDialog() {
    const reason = (this.statusAnnotationText() || '').trim();
    if (!reason) {
      this.alertService.error('Please enter a reason for the annotation.');
      return;
    }

    this.showStatusAnnotationDialog.set(false);
    this.saveProgramStatus(
      this.pendingProgramID!,
      this.pendingProgramStatus!,
      this.pendingPropagateStatus,
      reason.substring(0, this.statusAnnotationMaxLength)
    );
  }

  cancelStatusAnnotationDialog() {
    this.showStatusAnnotationDialog.set(false);
    this.saveProgramStatus(
      this.pendingProgramID!,
      this.pendingProgramStatus!,
      this.pendingPropagateStatus,
      null
    );
  }

  saveProgramStatus(
    programID: number,
    statusID: PatientProgramStatus,
    propagateStatus: boolean,
    statusAnnotationText: string | null
  ) {
    if (this.updatingStatus || !programID) {
      return;
    }
    this.updatingStatus = true;
    this.supervisorApiService
      .UpdateProgramStatus({
        programID: programID,
        statusID: statusID,
        propagateStatus: propagateStatus,
        statusAnnotationText: statusAnnotationText ?? undefined,
      })
      .subscribe({
        next: () => {
          this.updatingStatus = false;
          this.alertService.success('Program Status Updated');
          this.supervisorHelperService.triggerUpdateSignal();
        },
        error: (error) => {
          this.updatingStatus = false;
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
        },
      });
  }

  getProgramGoal() {
    if (this.program?.BSP_SkillAcquisitionGoalID) {
      return this.program.BSP_SkillAcquisitionGoalName;
    } else {
      return this.program?.SkillAcquisitionGoal_Other;
    }
  }

  // Full objective object from the latest BSP, so the card can show context
  // (skill area, BSP program, status, previous objective) even when the
  // objective has no description.
  getBspObjective() {
    const goalId = this.program?.BSP_SkillAcquisitionGoalID;
    if (!goalId) {
      return null;
    }
    return (
      this.supervisorHelperService
        .skillAcquisitionGoals()
        .find((goal) => goal.ID === goalId) ?? null
    );
  }

  isProgramSelected(): boolean {
    if (this.programSelected === true && this.isSelecting === true) return true;

    return false;
  }

  showChangeLog(): void {
    if (this.changeLogModal) {
      this.changeLogModal.open();
    }
  }
}
